import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
// =============================================================================
// Grid Layout Component
// =============================================================================
const GridLayout = ({ layout, components, children }) => {
    const { grid, gap = 4, padding = 4 } = layout;
    if (!grid) {
        throw new Error('Grid layout requires grid configuration');
    }
    const { columns, rows, areas } = grid;
    // Generate CSS Grid template areas from the areas array
    const gridTemplateAreas = areas ? areas.map((row) => `"${row.join(' ')}"`).join(' ') : undefined;
    const gridStyle = {
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gap: `${gap * 0.25}rem`, // Convert to Tailwind spacing scale
        padding: `${padding * 0.25}rem`,
        ...(gridTemplateAreas && { gridTemplateAreas }),
    };
    return (_jsx("div", { style: gridStyle, className: 'w-full h-full', children: children.map((child, index) => {
            const component = components[index];
            if (!component) {
                return child;
            }
            const { layout: componentLayout } = component;
            const itemStyle = {};
            // Handle grid area assignment
            if (componentLayout?.gridArea) {
                itemStyle.gridArea = componentLayout.gridArea;
            }
            // Legacy support for explicit positioning (only available in CompositeComponentConfig)
            else if (componentLayout &&
                'position' in componentLayout &&
                'span' in componentLayout &&
                componentLayout.position &&
                componentLayout.span) {
                const { position, span } = componentLayout;
                itemStyle.gridColumn = `${position.column} / span ${span.colSpan || 1}`;
                itemStyle.gridRow = `${position.row} / span ${span.rowSpan || 1}`;
            }
            return (_jsx("div", { style: itemStyle, className: 'min-h-0 min-w-0 flex items-center justify-center p-2', children: child }, component.id));
        }) }));
};
// =============================================================================
// Stack Layout Component
// =============================================================================
const StackLayout = ({ layout, components, children }) => {
    const { direction = 'column', gap = 4, padding = 4 } = layout;
    const stackStyle = {
        display: 'flex',
        flexDirection: direction,
        gap: `${gap * 0.25}rem`, // Convert to Tailwind spacing scale
        padding: `${padding * 0.25}rem`,
    };
    return (_jsx("div", { style: stackStyle, className: 'w-full h-full', children: children.map((child, index) => {
            const component = components[index];
            return (_jsx("div", { className: 'min-h-0 min-w-0 flex-1', children: child }, component?.id || index));
        }) }));
};
// =============================================================================
// Flex Layout Component
// =============================================================================
const FlexLayout = ({ layout, components, children }) => {
    const { direction = 'row', gap = 4, padding = 4 } = layout;
    const flexStyle = {
        display: 'flex',
        flexDirection: direction,
        gap: `${gap * 0.25}rem`, // Convert to Tailwind spacing scale
        padding: `${padding * 0.25}rem`,
    };
    return (_jsx("div", { style: flexStyle, className: 'w-full h-full', children: children.map((child, index) => {
            const component = components[index];
            if (!component) {
                return child;
            }
            const { layout: componentLayout } = component;
            const flex = componentLayout?.flex;
            const order = componentLayout?.order;
            const itemStyle = {
                ...(flex && {
                    flexGrow: flex.grow,
                    flexShrink: flex.shrink,
                    flexBasis: flex.basis,
                }),
                ...(order && { order }),
            };
            const itemClasses = classNames('min-h-0 min-w-0', !flex && 'flex-1' // Default to equal distribution if no flex specified
            );
            return (_jsx("div", { style: itemStyle, className: itemClasses, children: child }, component.id));
        }) }));
};
// =============================================================================
// Layout Component Mappings
// =============================================================================
const LAYOUT_COMPONENTS = {
    grid: GridLayout,
    stack: StackLayout,
    flex: FlexLayout,
};
// =============================================================================
// Main Composite Layout Component
// =============================================================================
export const CompositeLayout = ({ layout, components, children }) => {
    const { type } = layout;
    const LayoutComponent = LAYOUT_COMPONENTS[type];
    if (!LayoutComponent) {
        console.error(`Unknown layout type: ${type}`);
        return (_jsxs("div", { className: 'p-4 text-red-500 bg-red-50 rounded', children: [_jsxs("p", { children: ["Error: Unknown layout type \"", type, "\""] }), _jsx("p", { children: "Supported types: grid, stack, flex" })] }));
    }
    return (_jsx(LayoutComponent, { layout: layout, components: components, children: children }));
};
